import React, { useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import useLabJobs from '../../utils/useLabJobs';
import {
  LAB_FLAG_META,
  LabAvatar,
  LabFlagChip,
  LabJobForm,
  LabJobThread,
  LabStatusBadge,
  labFullDate,
  labTimeAgo,
} from '../../components/LabJobParts';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'in process', label: 'In process' },
  { key: 'on hold', label: 'On hold' },
  { key: 'solved', label: 'Solved' },
  { key: 'all', label: 'All' },
];

const EMPTY_TEXT = {
  pending: 'Nothing is waiting in the queue.',
  'in process': 'The lab assistant is not working on anything right now.',
  'on hold': 'No jobs are on hold.',
  solved: 'No jobs solved in the last 30 days.',
  all: 'No jobs yet. Add the first one above.',
};

// Shown to users who only see the jobs they added themselves.
const OWN_EMPTY_TEXT = {
  pending: 'None of your jobs are waiting in the queue.',
  'in process': 'None of your jobs are being worked on right now.',
  'on hold': 'None of your jobs are on hold.',
  solved: 'None of your jobs were solved in the last 30 days.',
  all: 'You have not added any jobs yet. Add the first one above.',
};

export default function LabJobs() {
  const {
    jobs, viewer, loading, loadError,
    createJob, updateJob, deleteJob, addComment, deleteComment,
  } = useLabJobs();

  const [tab, setTab] = useState('pending');
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const counts = useMemo(() => {
    const c = { all: jobs.length, pending: 0, 'in process': 0, 'on hold': 0, solved: 0 };
    jobs.forEach((j) => { c[j.status] += 1; });
    return c;
  }, [jobs]);

  const visible = useMemo(() => {
    const list = tab === 'all' ? jobs : jobs.filter((j) => j.status === tab);
    // Pending shows in queue order (oldest first); everything else newest first.
    return [...list].sort((a, b) => (tab === 'pending'
      ? a.queuePosition - b.queuePosition
      : new Date(b.updatedAt) - new Date(a.updatedAt)));
  }, [jobs, tab]);

  const handleCreate = async (payload) => {
    setBusy(true);
    const ok = await createJob(payload);
    setBusy(false);
    if (ok) {
      setTab('pending');
      setShowForm(false);
    }
    return ok;
  };

  const handleEdit = async (jobId, payload) => {
    setBusy(true);
    const ok = await updateJob(jobId, payload);
    setBusy(false);
    if (ok) setEditingId(null);
    return ok;
  };

  const handleDelete = async (job) => {
    const confirm = await Swal.fire({
      title: viewer.isSuperAdmin ? 'Delete this job?' : 'Cancel this job?',
      text: `"${job.title}" will be removed from the queue.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: viewer.isSuperAdmin ? 'Delete' : 'Cancel job',
      cancelButtonText: 'Keep it',
    });
    if (!confirm.isConfirmed) return;
    await deleteJob(job.jobId);
  };

  const canEdit = (job) => viewer.isManager || (job.status === 'pending' && job.creator?.staffId === viewer.currentStaffId);
  const canRemove = (job) => viewer.isSuperAdmin || (job.status === 'pending' && job.creator?.staffId === viewer.currentStaffId);

  return (
    <DashboardLayout title="Lab Jobs">
      <div className="mx-auto max-w-5xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 px-6 py-5 text-white shadow-sm">
          <div>
            <h1 className="text-xl font-bold">Lab Jobs</h1>
            <p className="mt-1 text-sm text-teal-50">Queue work for the lab assistant. Jobs are taken in order, and a "Very important" flag lets it jump the line.</p>
            {!loading && !viewer.canSeeAll && (
              <p className="mt-1 text-sm font-semibold text-white">You can see the jobs you added and their updates.</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="rounded-lg bg-white px-4 py-2 text-sm font-bold text-teal-700 shadow-sm transition hover:bg-teal-50"
          >
            {showForm ? 'Close' : '+ Add a job'}
          </button>
        </div>

        {showForm && (
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-base font-semibold text-gray-900">New job for the lab assistant</h2>
            <LabJobForm submitLabel="Add to queue" busy={busy} onSubmit={handleCreate} />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${tab === t.key ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'}`}
            >
              {t.label} <span className={`ml-1 text-xs ${tab === t.key ? 'text-teal-100' : 'text-gray-400'}`}>{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-10 text-center text-sm text-gray-400">Loading…</p>
        ) : loadError ? (
          <p className="py-10 text-center text-sm text-red-600">{loadError}</p>
        ) : visible.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-12 text-center text-sm text-gray-500">{(viewer.canSeeAll ? EMPTY_TEXT : OWN_EMPTY_TEXT)[tab]}</div>
        ) : (
          <div className="space-y-3">
            {visible.map((job) => {
              const open = openId === job.jobId;
              const editing = editingId === job.jobId;
              const flagMeta = LAB_FLAG_META[job.flag] || LAB_FLAG_META.normal;
              return (
                <div key={job.jobId} className={`overflow-hidden rounded-xl border border-gray-200 border-l-4 bg-white shadow-sm ${flagMeta.stripe}`}>
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : job.jobId)}
                    className="flex w-full items-start gap-3 px-4 py-3.5 text-left hover:bg-gray-50"
                  >
                    {job.queuePosition ? (
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-800" title="Position in the queue">
                        {job.queuePosition}
                      </span>
                    ) : (
                      <span className="mt-0.5 h-8 w-8 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900">{job.title}</span>
                        <LabFlagChip flag={job.flag} />
                        <LabStatusBadge status={job.status} />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {job.creator?.name || 'Someone'} · {labTimeAgo(job.createdAt)}
                        {job.location ? ` · ${job.location}` : ''}
                        {job.handler && job.status !== 'pending' ? ` · Lab: ${job.handler.name}` : ''}
                        {job.comments.length ? ` · ${job.comments.length} update${job.comments.length === 1 ? '' : 's'}` : ''}
                      </p>
                    </div>
                    <svg className={`mt-1 h-5 w-5 shrink-0 text-gray-400 transition ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {open && (
                    <div className="space-y-4 border-t border-gray-100 bg-gray-50/60 px-5 py-4">
                      {editing ? (
                        <LabJobForm
                          initial={job}
                          submitLabel="Save changes"
                          busy={busy}
                          onSubmit={(payload) => handleEdit(job.jobId, payload)}
                          onCancel={() => setEditingId(null)}
                        />
                      ) : (
                        <>
                          <div className="flex items-start gap-3">
                            <LabAvatar person={job.creator} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900">{job.creator?.name || 'Unknown'}</p>
                              <p className="text-xs text-gray-500" title={labFullDate(job.createdAt)}>Requested {labFullDate(job.createdAt)}</p>
                              <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800">{job.description || <span className="text-gray-400">No extra details.</span>}</p>
                              {job.location && <p className="mt-2 text-xs text-gray-500">Place: <span className="font-semibold text-gray-700">{job.location}</span></p>}
                              {job.status === 'solved' && job.solvedAt && (
                                <p className="mt-2 text-xs text-emerald-700">Solved {labFullDate(job.solvedAt)}{job.handler ? ` by ${job.handler.name}` : ''}</p>
                              )}
                            </div>
                          </div>

                          {(canEdit(job) || canRemove(job)) && (
                            <div className="flex flex-wrap gap-2">
                              {canEdit(job) && (
                                <button type="button" onClick={() => setEditingId(job.jobId)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                                  Edit
                                </button>
                              )}
                              {canRemove(job) && (
                                <button type="button" onClick={() => handleDelete(job)} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
                                  {viewer.isSuperAdmin ? 'Delete' : 'Cancel job'}
                                </button>
                              )}
                            </div>
                          )}
                          {!canEdit(job) && job.status !== 'pending' && job.status !== 'solved' && (
                            <p className="text-xs text-gray-400">The lab assistant has picked this up, so it can no longer be edited or cancelled.</p>
                          )}
                        </>
                      )}

                      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                        <LabJobThread
                          job={job}
                          currentStaffId={viewer.currentStaffId}
                          isSuperAdmin={viewer.isSuperAdmin}
                          onComment={addComment}
                          onDeleteComment={deleteComment}
                          composerPlaceholder="Add a note for the lab assistant…  (Enter to post)"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
